import React, { useState } from 'react';
import { 
    FiShield, FiTrendingUp, FiCheckCircle, FiXCircle, FiArrowRight, 
    FiLayers, FiDollarSign, FiPieChart, FiActivity, FiMapPin, FiBriefcase, FiUsers,
    FiLink, FiHome, FiGitPullRequest, FiTarget, FiCheck
} from 'react-icons/fi';
import TopBanner from '../../../components/topBanner/topBanner';
import bannerImg from '../../../images/banner/banner---gioi-thieu.png';
import TypicalProjectSlider from '../typicalProject/TypicalProjectSlider';
import { 
    getHsvMarketOpportunity, 
    getHsvMarketBarriers,
    getHsvPppStructure,
    getHsvHoldingStructure, 
    getHsvHospitalNetwork,
    getHsvRevenueOptimizationMatrix, 
    getHsvCapitalStrategy,
    getHsvStrategicVision,
    getHsvCoreBusinessStrategy,
    getHsvRevenueOptimizationDetail
} from './hsvCorpData';
import CoreBusinessStrategyFlow from './CoreBusinessStrategyFlow';
import RevenueOptimizationDetail from './RevenueOptimizationDetail';

import HsvMatrixReactFlow from './react-flow/HsvMatrixReactFlow';
import HsvOrgChartReactFlow from './react-flow/HsvOrgChartReactFlow';
import HsvPyramidReactFlow from './react-flow/HsvPyramidReactFlow';
import './ecosystem-detail.scss';

function HsvCorpOverview({ isEnglish }) {
    const [activeSection, setActiveSection] = useState('hsv-corp');
    const [activeHoldingTab, setActiveHoldingTab] = useState(1);
    const [activeRevenueTab, setActiveRevenueTab] = useState(0);

    const marketOpp = getHsvMarketOpportunity(isEnglish);
    const marketBarriers = getHsvMarketBarriers(isEnglish);
    const pppStruct = getHsvPppStructure(isEnglish);
    const holdingStruct = getHsvHoldingStructure(isEnglish);
    const hospitalNetwork = getHsvHospitalNetwork(isEnglish);
    const revenueMatrix = getHsvRevenueOptimizationMatrix(isEnglish);
    const capitalStrategy = getHsvCapitalStrategy(isEnglish);
    const strategicVision = getHsvStrategicVision(isEnglish);
    const coreBusiness = getHsvCoreBusinessStrategy(isEnglish);

    const barrierIcons = [<FiDollarSign />, <FiMapPin />, <FiUsers />, <FiActivity />, <FiShield />];

    const scrollToSection = (id) => {
        setActiveSection(id);
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <main className="ecosystem-detail hsv-corp-overview">
            <TopBanner
                titleCenter="HSV Corp"
                imgBanner={bannerImg}
                pageName={isEnglish ? "Ecosystem" : "Hệ sinh thái"}
                text="HSV Corp"
            />

            {/* Sticky Floating Glassmorphism Navigation Bar */}
            <div className="layout_container" style={{ marginTop: '24px' }}>
                <nav className="hsv-dribbble-nav">
                    <button
                        type="button"
                        className={`hsv-dribbble-nav__item ${activeSection === 'hsv-corp' ? 'hsv-dribbble-nav__item--active' : ''}`}
                        onClick={() => scrollToSection('sec-hsv-corp')}
                    >
                        <FiShield /> {isEnglish ? '1. HSV Corp & PPP Model' : 'Phần 1: HSV Corp'}
                    </button>
                    <button
                        type="button"
                        className={`hsv-dribbble-nav__item ${activeSection === 'business-strategy' ? 'hsv-dribbble-nav__item--active' : ''}`}
                        onClick={() => scrollToSection('sec-business-strategy')}
                    >
                        <FiTrendingUp /> {isEnglish ? '2. Business Strategy' : 'Phần 2: Chiến Lược Kinh Doanh'}
                    </button>
                </nav>
            </div>

            {/* ========================================================================= */}
            {/* PHẦN 1: HSV CORP - GIỚI THIỆU TỔNG QUAN & MÔ HÌNH HỢP TÁC PPP (SLIDE DATA) */}
            {/* ========================================================================= */}
            <div id="sec-hsv-corp">
                {/* Dribbble Hero Banner Section */}
                <section className="layout_container">
                    <div className="hsv-hero-banner">
                        <div className="hsv-hero-banner__content">
                            <span className="hsv-hero-banner__tag">
                                <FiShield /> {isEnglish ? 'PART 1: HSV CORP HOLDINGS' : 'PHẦN 1: GIỚI THIỆU TỔNG QUAN HSV CORP'}
                            </span>
                            <h1>{isEnglish ? 'Healthcare Infrastructure & Target-Oriented PPP Hospital Management' : 'Tập Đoàn HSV Corp - Nền Tảng Hạ Tầng Y Tế & Mô Hình Hợp Tác Công Tư (PPP)'}</h1>
                            <p>
                                {isEnglish
                                    ? 'HSV Corp operates under the Strategic Control Holding model, managing healthcare networks, hospital chains, and high-tech medical solutions.'
                                    : 'HSV Corp vận hành theo mô hình Tập đoàn Holding (Kiểm soát chiến lược), xây dựng chuỗi bệnh viện hợp tác PPP chuẩn hóa và tối ưu hóa quản trị y tế toàn diện.'}
                            </p>
                        </div>
                    </div>
                </section>

                {/* 1. Market Opportunity Comparison (Public Hospital vs PPP Partner Hospital) */}
                <section className="layout_container ecosystem-detail__services" style={{ paddingTop: '10px' }}>
                    <div className="ecosystem-detail__heading">
                        <div>
                            <span>{isEnglish ? 'MARKET OPPORTUNITY' : 'CƠ HỘI GIA NHẬP THỊ TRƯỜNG'}</span>
                            <h2>{marketOpp.title}</h2>
                            <p className="ecosystem-detail__subtext">{marketOpp.subtitle}</p>
                        </div>
                    </div>

                    <div className="hsv-bento-comparison">
                        {/* Public Hospital Card */}
                        <div className="hsv-bento-comparison__card hsv-bento-comparison__card--public">
                            <span className="hsv-bento-comparison__card__badge hsv-bento-comparison__card__badge--danger">
                                {marketOpp.publicHospital.badge}
                            </span>
                            <h3>{marketOpp.publicHospital.title}</h3>
                            <ul className="hsv-bento-comparison__card__list">
                                {marketOpp.publicHospital.items.map((item, idx) => (
                                    <li key={idx}>
                                        <FiXCircle className="icon-fail" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* PPP Partner Hospital Card */}
                        <div className="hsv-bento-comparison__card hsv-bento-comparison__card--partner">
                            <span className="hsv-bento-comparison__card__badge hsv-bento-comparison__card__badge--success">
                                {marketOpp.partnerHospital.badge}
                            </span>
                            <h3>{marketOpp.partnerHospital.title}</h3>
                            <ul className="hsv-bento-comparison__card__list">
                                {marketOpp.partnerHospital.items.map((item, idx) => (
                                    <li key={idx}>
                                        <FiCheckCircle className="icon-pass" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>

                {/* 1.5 Market Barriers Bento Grid */}
                <section className="layout_container ecosystem-detail__services" style={{ paddingTop: '20px' }}>
                    <div className="ecosystem-detail__heading">
                        <div>
                            <span>{isEnglish ? 'MARKET BARRIERS' : 'RÀO CẢN THỊ TRƯỜNG'}</span>
                            <h2>{marketBarriers.title}</h2>
                            <p className="ecosystem-detail__subtext">{marketBarriers.subtitle}</p>
                        </div>
                    </div>
                    <div className="hsv-barriers-grid">
                        {marketBarriers.items.map((item, idx) => (
                            <div key={idx} className="hsv-barriers-grid__card">
                                <div className="hsv-barriers-grid__card__header">
                                    <span className="hsv-barriers-grid__card__step">0{idx + 1}</span>
                                    <div className="hsv-barriers-grid__card__icon">
                                        {barrierIcons[idx % barrierIcons.length]}
                                    </div>
                                </div>
                                <h4>{item.title}</h4>
                                <p>{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* 1.5 Solution: PPP Capital & Expertise Structure */}
                <section className="layout_container ecosystem-detail__services" style={{ paddingTop: '40px' }}>
                    <div className="ecosystem-detail__heading">
                        <div>
                            <span style={{ color: '#0ea5e9', background: 'rgba(14, 165, 233, 0.1)' }}>
                                <FiTrendingUp style={{ marginRight: '6px' }} />
                                {isEnglish ? 'SOLUTION: PPP MODEL' : 'GIẢI PHÁP: MÔ HÌNH PPP'}
                            </span>
                            <h2>{pppStruct.title}</h2>
                            <p className="ecosystem-detail__subtext">{pppStruct.subtitle}</p>
                        </div>
                    </div>

                    <div className="hsv-ppp-funnel">
                        {pppStruct.phases && (
                            <>
                                {/* Phase 1: Inputs */}
                                <div className="ppp-phase ppp-phase--inputs">
                                    <div className="ppp-card ppp-card--hsv">
                                        <div className="ppp-card__header">
                                            <FiBriefcase className="icon-main" />
                                            <h3>{pppStruct.phases[0].hsv.name}</h3>
                                        </div>
                                        <ul className="ppp-card__list">
                                            {pppStruct.phases[0].hsv.items.map((item, idx) => (
                                                <li key={idx}>
                                                    <span className="label">{item.label}:</span>
                                                    <span className="text">{item.text}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    
                                    <div className="ppp-connector ppp-connector--merge">
                                        <div className="line line-left"></div>
                                        <div className="plus-node"><FiGitPullRequest /></div>
                                        <div className="line line-right"></div>
                                    </div>

                                    <div className="ppp-card ppp-card--public">
                                        <div className="ppp-card__header">
                                            <FiHome className="icon-main" />
                                            <h3>{pppStruct.phases[0].public.name}</h3>
                                        </div>
                                        <ul className="ppp-card__list">
                                            {pppStruct.phases[0].public.items.map((item, idx) => (
                                                <li key={idx}>
                                                    <span className="label">{item.label}:</span>
                                                    <span className="text">{item.text}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>

                                {/* Shared Items Phase (Merged Info) */}
                                <div className="ppp-phase ppp-phase--shared">
                                    <div className="ppp-connector ppp-connector--down">
                                        <div className="line-down"></div>
                                        <FiArrowRight className="arrow-down" />
                                    </div>
                                    
                                    <div className="ppp-card ppp-card--shared">
                                        <div className="ppp-card__header">
                                            <FiLink className="icon-main" />
                                            <h3>{pppStruct.phases[0].shared.title}</h3>
                                        </div>
                                        <ul className="ppp-card__list ppp-card__list--horizontal">
                                            {pppStruct.phases[0].shared.items.map((item, idx) => (
                                                <li key={idx}>
                                                    <span className="label">{item.label}</span>
                                                    <span className="text">{item.text}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>

                                {/* Phase 2: Core SPV */}
                                <div className="ppp-phase ppp-phase--core">
                                    <div className="ppp-connector ppp-connector--down">
                                        <div className="line-down"></div>
                                        <FiArrowRight className="arrow-down" />
                                    </div>
                                    
                                    <div className="ppp-card ppp-card--spv">
                                        <div className="ppp-card__header">
                                            <div className="spv-badge">SPV</div>
                                            <h3>{pppStruct.phases[1].title}</h3>
                                            <p>{pppStruct.phases[1].subtitle}</p>
                                        </div>
                                        <div className="ppp-card__body">
                                            <div className="core-function">
                                                <FiActivity className="pulse-icon" />
                                                <strong>{pppStruct.phases[1].coreFunction}</strong>
                                            </div>
                                            <ul className="feature-list">
                                                {pppStruct.phases[1].features.map((feat, idx) => (
                                                    <li key={idx}><FiCheckCircle /> {feat}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </section>



                {/* 1.6 Detailed Holding Structure (Tabs) */}
                <section className="layout_container ecosystem-detail__services" style={{ paddingTop: '20px' }}>
                    <div className="ecosystem-detail__heading">
                        <div>
                            <span>{isEnglish ? 'CORPORATE STRUCTURE' : 'CẤU TRÚC TẬP ĐOÀN'}</span>
                            <h2>{holdingStruct.title}</h2>
                            <p className="ecosystem-detail__subtext">{holdingStruct.subtitle}</p>
                        </div>
                    </div>

                    <div className="hsv-holding-container">
                        {/* Tab Navigation */}
                        <div className="hsv-holding-tabs">
                            {holdingStruct.tabs.map((tab, idx) => (
                                <button 
                                    key={idx} 
                                    className={`hsv-holding-tabs__btn ${activeHoldingTab === idx ? 'active' : ''}`}
                                    onClick={() => setActiveHoldingTab(idx)}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>

                        {/* Tab Content */}
                        <div className="hsv-holding-content">
                            
                            {/* TAB 0: Models */}
                            {activeHoldingTab === 0 && (
                                <div className="hsv-holding-models fade-in">
                                    <div className="hsv-holding-models__header">
                                        <h3>{holdingStruct.models.title}</h3>
                                        <p>{holdingStruct.models.desc}</p>
                                    </div>
                                    
                                    <div className="hsv-holding-models__axis">
                                        <span className="axis-left"><FiArrowRight style={{transform: 'rotate(180deg)'}} /> {holdingStruct.models.axis.left}</span>
                                        <div className="axis-line"></div>
                                        <span className="axis-right">{holdingStruct.models.axis.right} <FiArrowRight /></span>
                                    </div>

                                    <div className="hsv-holding-models__grid">
                                        {holdingStruct.models.items.map((item, idx) => (
                                            <div key={idx} className={`hsv-model-card color-${item.color}`}>
                                                <div className="hsv-model-card__title">
                                                    <h4>{item.type}</h4>
                                                    <span>{item.enType}</span>
                                                </div>
                                                <div className="hsv-model-card__body">
                                                    <div className="feature-block">
                                                        <FiTarget /> 
                                                        <p>{item.features}</p>
                                                    </div>
                                                    <div className="feature-block">
                                                        <FiDollarSign />
                                                        <p>{item.finance}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="hsv-holding-models__proposal">
                                        <h4>{holdingStruct.models.proposal.title}</h4>
                                        <ul>
                                            {holdingStruct.models.proposal.points.map((pt, idx) => (
                                                <li key={idx}><FiCheck /> {pt}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}

                            {/* TAB 1: Open Architecture (Pyramid) */}
                            {activeHoldingTab === 1 && (
                                <div className="hsv-holding-architecture fade-in">
                                    <div className="hsv-holding-architecture__header">
                                        <h3>{holdingStruct.architecture.title}</h3>
                                        <p>{holdingStruct.architecture.desc}</p>
                                    </div>

                                    <HsvPyramidReactFlow isEnglish={isEnglish} />
                                </div>
                            )}

                            {/* TAB 2: Org Chart */}
                            {activeHoldingTab === 2 && (
                                <div className="hsv-holding-org fade-in">
                                    <div className="hsv-holding-org__header">
                                        <h3>{holdingStruct.orgChart.title}</h3>
                                        <p>{holdingStruct.orgChart.desc}</p>
                                    </div>

                                    <HsvOrgChartReactFlow isEnglish={isEnglish} />
                                </div>
                            )}

                            {/* TAB 3: Structural Form */}
                            {activeHoldingTab === 3 && (
                                <div className="hsv-structural-form fade-in">
                                    <div className="hsv-structural-form__header">
                                        <h3>{holdingStruct.structuralForm.title}</h3>
                                        <p>{holdingStruct.structuralForm.desc}</p>
                                    </div>

                                    <div className="hsv-structural-matrix-reactflow">
                                        <HsvMatrixReactFlow isEnglish={isEnglish} />
                                    </div>
                                </div>
                            )}

                            {/* TAB 4: Finance */}
                            {activeHoldingTab === 4 && (
                                <div className="hsv-holding-finance fade-in">
                                    <div className="hsv-holding-finance__header">
                                        <span className="finance-header-tag">
                                            <FiShield /> {isEnglish ? 'HOLDING FINANCIAL POLICY' : 'QUẢN TRỊ TÀI CHÍNH CỐT LÕI'}
                                        </span>
                                        <h3>{holdingStruct.finance.title}</h3>
                                        <p>{holdingStruct.finance.desc}</p>
                                    </div>

                                    <div className="hsv-finance-categories-list">
                                        {holdingStruct.finance.categories.map((cat, idx) => {
                                            const icons = [<FiDollarSign />, <FiPieChart />, <FiTrendingUp />];
                                            const catIcon = icons[idx % icons.length];
                                            
                                            return (
                                                <div key={idx} className="finance-category-block">
                                                    <div className="finance-category-block__header">
                                                        <div className="cat-header-icon">{catIcon}</div>
                                                        <h4>{cat.name}</h4>
                                                        <span className="cat-badge">{cat.sub.length} {isEnglish ? 'Sub-policies' : 'Hạng mục'}</span>
                                                    </div>

                                                    <div className="finance-category-block__grid">
                                                        {cat.sub.map((sub, i) => (
                                                            <div key={i} className="finance-card">
                                                                <div className="finance-card__top">
                                                                    <span className="finance-card__type-badge">
                                                                        <FiLayers /> {sub.type}
                                                                    </span>
                                                                </div>
                                                                
                                                                <div className="finance-card__body">
                                                                    <div className="finance-box finance-box--target">
                                                                        <div className="box-title">
                                                                            <FiTarget className="icon-target" />
                                                                            <span>{isEnglish ? 'TARGET' : 'MỤC TIÊU'}</span>
                                                                        </div>
                                                                        <p>{sub.target}</p>
                                                                    </div>

                                                                    <div className="finance-box finance-box--choice">
                                                                        <div className="box-title">
                                                                            <FiCheckCircle className="icon-choice" />
                                                                            <span>{isEnglish ? 'CHOICE' : 'LỰA CHỌN THỰC THI'}</span>
                                                                        </div>
                                                                        <p>{sub.choice}</p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>
                </section>

                {/* 3. Hospital Chain Network List */}
                <section className="layout_container ecosystem-detail__services" style={{ paddingTop: '20px' }}>
                    {/* Strategic Vision For Hospital Network */}
                    <div className="hsv-strategic-vision-section">
                        <div className="hsv-strategic-vision-header">
                            <span className="vision-tag">
                                <FiShield /> {isEnglish ? 'STRATEGIC VISION' : 'TẦM NHÌN CHUỖI BỆNH VIỆN'}
                            </span>
                            <h3>{strategicVision.title}</h3>
                            <p>{strategicVision.subtitle}</p>
                        </div>

                        <div className="hsv-strategic-vision-grid">
                            {strategicVision.pillars.map((pillar, idx) => (
                                <div key={idx} className="hsv-vision-card">
                                    <div className="hsv-vision-card__left">
                                        <div className="hsv-vision-card__pill">
                                            <span className="pill-num">0{idx + 1}</span>
                                            <h5>{pillar.label}</h5>
                                        </div>
                                    </div>

                                    <div className="hsv-vision-card__right">
                                        <ul className="hsv-vision-bullets">
                                            {pillar.points.map((pt, i) => (
                                                <li key={i}>
                                                    <FiCheckCircle className="bullet-icon" />
                                                    <span>{pt}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="ecosystem-detail__heading">
                        <div>
                            <span>{isEnglish ? 'TARGET HOSPITAL NETWORK' : 'QUY MÔ CHUỖI BỆNH VIỆN TẬP ĐOÀN'}</span>
                            <h2>{isEnglish ? 'HSV Corp Hospital Network Roadmap (2026 - 2028)' : 'Danh Mục Chuỗi Bệnh Viện Trọng Điểm HSV Corp'}</h2>
                        </div>
                    </div>

                    <div className="hsv-network-grid">
                        {hospitalNetwork.map((hosp, idx) => (
                            <div key={idx} className="hsv-network-grid__card">
                                <div>
                                    <div className="hsv-network-grid__card__location">
                                        <FiMapPin /> {hosp.location}
                                    </div>
                                    <h4>{hosp.name}</h4>
                                    <span className="hsv-network-grid__card__type">{hosp.type}</span>
                                </div>
                                <div className="hsv-network-grid__card__footer">
                                    <span>{isEnglish ? 'Scale:' : 'Quy mô:'}</span>
                                    <strong>{hosp.beds} {isEnglish ? 'Beds' : 'Giường'}</strong>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Capital Timeline Roadmap */}
                    <div className="hsv-timeline-roadmap" style={{ marginTop: '40px' }}>
                        <div className="hsv-timeline-roadmap__header">
                            <span className="hsv-timeline-roadmap__tag">
                                <FiTrendingUp /> {isEnglish ? 'CAPITAL ROADMAP 2026-2028' : 'LỘ TRÌNH TĂNG VỐN & SỞ HỮU'}
                            </span>
                            <h3>{capitalStrategy.timeline.title}</h3>
                            <p>{capitalStrategy.timeline.desc}</p>
                        </div>
                        
                        <div className="hsv-timeline-roadmap__grid">
                            {capitalStrategy.timeline.periods.map((period, idx) => {
                                const hsvPercent = parseInt(period.hsvRatio) || 50;
                                const isFinal = idx === capitalStrategy.timeline.periods.length - 1;
                                return (
                                    <div key={idx} className={`hsv-timeline-roadmap__step ${isFinal ? 'hsv-timeline-roadmap__step--final' : ''}`}>
                                        <h4>{period.year}</h4>
                                        
                                        <div className="hsv-timeline-roadmap__step__total">{period.total}</div>
                                        
                                        <div className="hsv-timeline-roadmap__step__bars">
                                            <div className="bar-hsv" style={{ width: `${hsvPercent}%` }}></div>
                                            <div className="bar-investor" style={{ width: `${100 - hsvPercent}%` }}></div>
                                        </div>
                                        
                                        <div className="hsv-timeline-roadmap__step__details">
                                            <span>HSV: <strong>{period.hsvRatio}</strong></span>
                                            <span>NĐT: <strong>{period.investorRatio}</strong></span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            </div>

            {/* ========================================================================= */}
            {/* PHẦN 2: CHIẾN LƯỢC KINH DOANH - TỐI ĐA HÓA DOANH THU & QUẢN TRỊ TÀI CHÍNH */}
            {/* ========================================================================= */}
            <div id="sec-business-strategy">
                {/* 1. Revenue Maximization Strategy */}
                <section className="layout_container ecosystem-detail__services" style={{ paddingTop: '32px' }}>
                    <div className="ecosystem-detail__heading">
                        <div>
                            <span>{isEnglish ? 'PART 2: REVENUE STRATEGY' : 'PHẦN 2: CHIẾN LƯỢC KINH DOANH'}</span>
                            <h2>{isEnglish ? 'Revenue Maximization Strategy Across 3 Pillars' : 'Chiến Lược Tối Đa Hóa Doanh Thu Y Tế'}</h2>
                            <p className="ecosystem-detail__subtext">
                                {isEnglish 
                                    ? 'Expanding revenue streams across Medical, Non-medical (Hospitality), and Post-medical care.'
                                    : 'Tăng trưởng doanh thu toàn diện dựa trên 3 trụ cột: Y khoa cốt lõi (Medical), Dịch vụ lưu trợ (Non-medical) và Chăm sóc sau điều trị (Post-medical).'}
                            </p>
                        </div>
                    </div>

                    {/* Interactive Revenue Optimization Matrix */}
                    <div className="hsv-revenue-matrix">
                        {/* Left Tabs */}
                        <div className="hsv-revenue-matrix__tabs">
                            {revenueMatrix.factors.map((factor, idx) => (
                                <button 
                                    key={idx} 
                                    className={`hsv-revenue-matrix__tab ${activeRevenueTab === idx ? 'active' : ''}`}
                                    onClick={() => setActiveRevenueTab(idx)}
                                >
                                    <span>{factor.name}</span>
                                    <FiArrowRight />
                                </button>
                            ))}
                        </div>

                        {/* Right Content */}
                        <div className="hsv-revenue-matrix__content">
                            {revenueMatrix.factors.map((factor, idx) => (
                                <div 
                                    key={idx} 
                                    className={`hsv-revenue-matrix__panel ${activeRevenueTab === idx ? 'active' : ''}`}
                                >
                                    {factor.id === 'pricing' && factor.pricingEvolution && (
                                        <div className="hsv-pricing-evolution">
                                            {factor.pricingEvolution.map((step, i) => (
                                                <React.Fragment key={i}>
                                                    <div className="hsv-pricing-step">
                                                        <div className="hsv-pricing-circle">
                                                            {step.short}
                                                        </div>
                                                        <span className="hsv-pricing-label">{step.label}</span>
                                                    </div>
                                                    {i < factor.pricingEvolution.length - 1 && (
                                                        <div className="hsv-pricing-arrow">
                                                            <FiArrowRight />
                                                        </div>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    )}

                                    <div className="hsv-revenue-matrix__columns">
                                        {factor.shared && factor.shared.length > 0 && (
                                            <div className="hsv-revenue-column">
                                                <h4>
                                                    <FiTarget /> 
                                                    {isEnglish ? 'General Growth Factors' : 'Nhân Tố Tăng Trưởng Chung'}
                                                </h4>
                                                <ul>
                                                    {factor.shared.map((item, i) => (
                                                        <li key={i}>
                                                            <FiCheckCircle /> <span>{item}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        {factor.medical && factor.medical.length > 0 && (
                                            <div className="hsv-revenue-column">
                                                <h4>
                                                    <FiActivity /> 
                                                    {isEnglish ? 'Medical Services' : 'Lâm Sàng & Cận Lâm Sàng'}
                                                </h4>
                                                <ul>
                                                    {factor.medical.map((item, i) => (
                                                        <li key={i}>
                                                            <FiCheckCircle /> <span>{item}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {factor.nonMedical && factor.nonMedical.length > 0 && (
                                            <div className="hsv-revenue-column">
                                                <h4>
                                                    <FiBriefcase /> 
                                                    {isEnglish ? 'Post-medical & Hospitality' : 'Lưu Trú & Điều Dưỡng'}
                                                </h4>
                                                <ul>
                                                    {factor.nonMedical.map((item, i) => (
                                                        <li key={i}>
                                                            <FiCheckCircle /> <span>{item}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

            </div>

            {/* Core Business Strategy Section */}
            <section className="hsv-core-business-section" style={{ padding: '40px 0', background: 'transparent' }}>
                <div className="layout_container">
                    <div className="ecosystem-detail__heading" style={{ marginBottom: '28px' }}>
                        <div>
                            <span style={{ 
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                gap: '8px', 
                                padding: '6px 16px', 
                                background: 'rgba(77, 183, 72, 0.12)', 
                                color: '#278024', 
                                borderRadius: '50px', 
                                fontSize: '0.85rem', 
                                fontWeight: '700', 
                                letterSpacing: '0.08em', 
                                textTransform: 'uppercase' 
                            }}>
                                <FiLayers style={{ fontSize: '1rem' }} />
                                {isEnglish ? 'HSV ECOSYSTEM' : 'HỆ SINH THÁI HSV'}
                            </span>
                            <h2 style={{ 
                                fontSize: 'clamp(2rem, 3vw, 2.75rem)', 
                                fontWeight: '600', 
                                color: '#0f172a', 
                                marginTop: '14px', 
                                letterSpacing: '-0.02em', 
                                lineHeight: '1.2' 
                            }}>
                                {coreBusiness.title}
                            </h2>
                            <p style={{ 
                                marginTop: '16px', 
                                fontSize: '1.05rem', 
                                color: '#475569', 
                                maxWidth: '960px', 
                                lineHeight: '1.7', 
                                background: '#ffffff', 
                                padding: '16px 24px', 
                                borderRadius: '12px', 
                                borderLeft: '4px solid #4db748',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                            }}>
                                {coreBusiness.description}
                            </p>
                        </div>
                    </div>
                    <CoreBusinessStrategyFlow data={coreBusiness} />
                </div>
            </section>

            {/* Revenue Maximization Detail Section (New Slide Content) */}
            <RevenueOptimizationDetail data={getHsvRevenueOptimizationDetail(isEnglish)} />


            {/* Featured Slider Projects */}
            <section className="ecosystem-detail__projects">
                <div className="layout_container">
                <div className="ecosystem-detail__heading" style={{ marginBottom: '36px' }}>
                    <div>
                        <span>{isEnglish ? 'FEATURED PROJECTS' : 'DỰ ÁN TIÊU BIỂU'}</span>
                        <h2>{isEnglish ? 'HSV Corp Healthcare & Strategy Projects' : 'Dự Án Y Tế & Chiến Lược Tiêu Biểu HSV Corp'}</h2>
                    </div>
                </div>
                <TypicalProjectSlider variant="ecosystem" />
                </div>
            </section>
        </main>
    );
}

export default HsvCorpOverview;
